import {
  parseChartCollectionFromRaw,
  parseCitedSources,
  parseGeneratedQueriesFromRaw,
  parseTabularDataFromRaw,
  type ChatChartCollection,
  type AssistantTurnData,
  type ChatTabularData,
  type GeneratedQuery,
  type VerboseStepItem,
} from "@/features/chat/model/chatTypes";
import type { CitedSource } from "@/shared/lib/citations";
import {
  ANSWER_PART_EVENT,
  ANSWER_VALIDATED_EVENT,
  type TurnStreamEvent,
} from "@/features/chat/lib/turnStream";

/**
 * Pieces of an answer the backend sends as soon as each is ready (`answer_part` events):
 * the table and SQL right after the query runs, the chart once it is designed, the insights
 * when they are written. Each carries the `revision` of the SQL run it belongs to; parts sent
 * before the answer validator approves are provisional, and a retry that re-runs SQL sends a
 * higher revision that replaces them. `answer_validated` confirms a revision. The finished
 * turn (`done` + refetch) still carries the complete answer -- these are an early view of it.
 */

export type AnswerPartInsights = {
  summary: string | null;
  insights: string[];
  why: string | null;
  whyPoints: string[];
  action: string | null;
  actionPoints: string[];
  businessImpact: string | null;
  sources: CitedSource[];
  /** Why and action are still being written after the why research. */
  narrativePending: boolean;
  /** The research revised business impact after it was first written. */
  businessImpactUpdated: boolean;
};

export type StreamAnswerParts = {
  /** Highest SQL revision seen; parts from an older revision are stale. */
  revision: number;
  /** Highest revision the answer validator has approved. */
  validatedRevision: number;
  tabularData: ChatTabularData | null;
  chartData: ChatChartCollection | null;
  generatedQueries: GeneratedQuery[] | null;
  insights: AnswerPartInsights | null;
};

export const EMPTY_ANSWER_PARTS: StreamAnswerParts = {
  revision: 0,
  validatedRevision: 0,
  tabularData: null,
  chartData: null,
  generatedQueries: null,
  insights: null,
};

function stringOrNull(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string" && item.trim().length > 0)
    : [];
}

function parseInsightsPart(raw: unknown): AnswerPartInsights | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const record = raw as Record<string, unknown>;
  return {
    summary: stringOrNull(record.summary),
    insights: stringList(record.insights),
    why: stringOrNull(record.why),
    whyPoints: stringList(record.why_points),
    action: stringOrNull(record.action),
    actionPoints: stringList(record.action_points),
    businessImpact: stringOrNull(record.business_impact),
    sources: parseCitedSources(record.sources),
    narrativePending: record.narrative_pending === true,
    businessImpactUpdated: record.business_impact_updated === true,
  };
}

export function isAnswerPartEvent(event: TurnStreamEvent): boolean {
  return event.type === ANSWER_PART_EVENT || event.type === ANSWER_VALIDATED_EVENT;
}

/** Folds one `answer_part` / `answer_validated` event in. Idempotent, so replays are safe. */
export function applyAnswerPartEvent(
  current: StreamAnswerParts,
  event: TurnStreamEvent,
): StreamAnswerParts {
  if (event.type === ANSWER_VALIDATED_EVENT) {
    const approved = event.revision ?? current.revision;
    return { ...current, validatedRevision: Math.max(current.validatedRevision, approved) };
  }
  if (event.type !== ANSWER_PART_EVENT || !event.part) {
    return current;
  }

  const revision = event.revision ?? 0;
  if (revision < current.revision) {
    return current;
  }
  // A newer SQL run replaces everything built on the old one; its own parts follow.
  const base: StreamAnswerParts =
    revision > current.revision
      ? { ...current, revision, tabularData: null, chartData: null, generatedQueries: null }
      : current;
  const validatedRevision =
    event.provisional === false
      ? Math.max(base.validatedRevision, revision)
      : base.validatedRevision;

  switch (event.part) {
    case "table":
      return { ...base, validatedRevision, tabularData: parseTabularDataFromRaw(event.answer_part) };
    case "chart":
      return {
        ...base,
        validatedRevision,
        chartData: parseChartCollectionFromRaw(event.answer_part),
      };
    case "queries":
      return {
        ...base,
        validatedRevision,
        generatedQueries: parseGeneratedQueriesFromRaw(event.answer_part),
      };
    case "insights":
      return { ...base, validatedRevision, insights: parseInsightsPart(event.answer_part) };
    default:
      return base;
  }
}

export function hasAnswerParts(parts: StreamAnswerParts): boolean {
  return Boolean(
    parts.tabularData ||
      parts.chartData ||
      (parts.generatedQueries && parts.generatedQueries.length > 0) ||
      parts.insights,
  );
}

/** True while a part on screen belongs to a SQL run the validator has not approved yet. */
export function areAnswerPartsProvisional(parts: StreamAnswerParts): boolean {
  return parts.revision > parts.validatedRevision;
}

/**
 * The parts as turn fields. Only parts that have arrived are set, so spreading this over a
 * turn never blanks something the turn already shows.
 */
export function answerPartsToAssistantData(
  parts: StreamAnswerParts,
): Partial<AssistantTurnData> {
  const insights = parts.insights;
  return {
    ...(parts.tabularData ? { tabularData: parts.tabularData } : {}),
    ...(parts.chartData ? { chartData: parts.chartData } : {}),
    ...(parts.generatedQueries && parts.generatedQueries.length > 0
      ? { generatedQueries: parts.generatedQueries }
      : {}),
    ...(insights
      ? {
          summary: insights.summary,
          insights: insights.insights,
          why: insights.why,
          whyPoints: insights.whyPoints,
          action: insights.action,
          actionPoints: insights.actionPoints,
          businessImpact: insights.businessImpact,
          sources: insights.sources,
          narrativePending: insights.narrativePending,
          businessImpactUpdated: insights.businessImpactUpdated,
        }
      : {}),
    answerPartsProvisional: areAnswerPartsProvisional(parts),
  };
}

/**
 * The answer parts a turn had already sent, rebuilt from its stored steps.
 *
 * A page reloaded mid-turn reconnects to the stream after the last stored step, so the
 * `answer_part` events that carried the table and SQL are not sent again -- and the turn row
 * only gets its `tabular_data` on completion. The stored steps still hold every part's payload,
 * so the reconnect starts from the same parts the first view had.
 */
export function answerPartsFromStoredSteps(items: VerboseStepItem[]): StreamAnswerParts {
  return items.reduce<StreamAnswerParts>((current, item) => {
    const payload = item.payload ?? {};
    const type = typeof payload.event_type === "string" ? payload.event_type : item.step_type;
    if (type !== ANSWER_PART_EVENT && type !== ANSWER_VALIDATED_EVENT) {
      return current;
    }
    return applyAnswerPartEvent(current, {
      type,
      part: typeof payload.part === "string" ? payload.part : undefined,
      revision: typeof payload.revision === "number" ? payload.revision : undefined,
      provisional: typeof payload.provisional === "boolean" ? payload.provisional : undefined,
      answer_part: payload.answer_part,
    });
  }, EMPTY_ANSWER_PARTS);
}

